/**
 * The main deliverable of a turn, and whether it has been saved — the rules
 * behind the old web's bottom-of-message "Save Skill" card (item BR).
 *
 * `Message.tsx` in the old web paired every successful `send_code_to_revit`
 * artifact with its execution result, then took the **last one whose intent
 * was `deliver`** as the message's main result and drew the card for it under
 * the reply. Investigations (`intent: "investigate"`) never got a card: the
 * code that answered a question is not the thing the user wants to keep.
 *
 * In 2.5 a step carries its artifact only when the run succeeded (`chat.ts`),
 * so "successful" is already implied by the artifact being there.
 *
 * The saved registry replaces the old web's per-message `isArtifactSaved`
 * array. The card and the step row both open the same source modal, from two
 * different components, and once an artifact has been saved from either, the
 * other must say so — saving twice is two skills. The artifact object is the
 * key: it is created once by the stream reducer and kept by reference in the
 * message state, so it identifies the run for as long as the transcript lives.
 */
import type { AgentStep } from "./chat";
import { generatesCode } from "./toolLabel";

/** A finished step whose artifact is a deliverable — what the row's Save Skill button and the reply's card are for. */
export function isDeliverStep(step: AgentStep | undefined): boolean {
  return (
    !!step &&
    generatesCode(step.tool) &&
    step.status === "completed" &&
    !!step.artifact &&
    step.artifact["intent"] === "deliver" &&
    typeof step.artifact["code"] === "string"
  );
}

/** Index of the message's main deliverable, or -1 when there is none. */
export function mainDeliverStepIndex(steps: readonly AgentStep[] | undefined): number {
  if (!steps) return -1;
  for (let i = steps.length - 1; i >= 0; i--) {
    if (isDeliverStep(steps[i])) return i;
  }
  return -1;
}

/** What the card calls the artifact: display name, else name, else the caller's fallback. */
export function artifactTitle(artifact: Record<string, unknown> | undefined, fallback: string): string {
  if (!artifact) return fallback;
  const displayName = artifact["displayName"];
  if (typeof displayName === "string" && displayName.trim()) return displayName.trim();
  const name = artifact["name"];
  if (typeof name === "string" && name.trim()) return name.trim();
  return fallback;
}

const saved = new WeakMap<object, string>();

/** Record that this artifact became a skill, under the name the store gave it. */
export function rememberSavedSkill(artifact: object, name: string): void {
  saved.set(artifact, name);
}

/** The saved skill's name, or null when this artifact has not been saved. */
export function savedSkillName(artifact: object | undefined): string | null {
  if (!artifact) return null;
  return saved.get(artifact) ?? null;
}
